import { string, mixed, date, object, number, array } from 'yup';
import { subYears, endOfDay, format } from 'date-fns';

const transformOption = (label: string) => ({
  label,
  value: label,
});

const yesNo = [
  'Yes',
  'No',
].map(transformOption);

const typeOptions = [
  'Personal Residence',
  'Other Residence',
  'Group Home',
  'Business',
  'Service Provider',
  'Public Property',
].map(transformOption);

const crisisEventOptions = [
  'Addiction Related',
  'Anxiety Related',
  'Autism Spectrum',
  'Behavioral Concerns',
  'Court Order',
  'Dementia',
  'Depression',
  'Depression/Hallucinations',
  'Homeless',
  'Intellectual Disability',
  'Non-suicidal Self-injury',
  'Trauma Related',
  'Suicide Thoughts',
  'Suicide Threat',
  'Suicide Attempt',
  'Suicide Completed',
  'Threat to Others',
  'Other',
].map(transformOption);

const responseOptions = [
  'No Contact',
  'Active Listening/De-escalation',
  'Force Used',
].map(transformOption);

const weaponInvolvedOptions = [
  'Firearm',
  'Edged Weapon',
  'Makeshift',
  'Other',
].map(transformOption);

const transportByOptions = [
  'Law Enforcement',
  'Ambulance',
  'No Transport',
  'Other',
].map(transformOption);

const processInitiatedByOptions = [
  'Law Enforcement',
  'Health Officer',
  'Physician/Medical Professional',
  'Other',
].map(transformOption);

const incidentHasRelatedCrimeOptions = [
  'Misdemeanor',
  'Felony',
].map(transformOption);

const criminalChargesToBeFiledOptions = [
  'Yes',
  'No',
  'TBD',
].map(transformOption);

const forceUsedIncidentResultedInInjuryOptions = [
  'Subject Injured',
  'Officer Injured',
  'Other Injured',
  'No Injury',
].map(transformOption);

const phoneTypeOptions = [
  'Mobile',
  'Work',
  'Home',
].map(transformOption);

const veteranStatuses = [{
  label: 'Veteran status unknown',
  value: 'Veteran status unknown',
}, {
  label: 'US Military/Veteran', // badge label
  value: 'Military/Veteran', // badge name
}, {
  label: 'Not US Military/Veteran',
  value: 'Not US Military/Veteran',
}];

const objectShape = {
  phoneNumber: string().min(10).required(),
  phoneType: mixed().required(),
  veteranStatus: mixed().required(),
  birthDate: date().min(format(subYears(new Date(), 100), 'MM/DD/YYYY')).max(format(endOfDay(new Date()), 'MM/DD/YYYY'))
    .nullable(),
  dateTime: date().required()
    .when('birthDate', (birthDate:Date, schema:any) => {
      const endOfBirthDate = endOfDay(birthDate);
      return birthDate ? date().min(endOfBirthDate, 'Incident date cannot be on or before Birth Date') : schema;
    }),
  firstName: string().required(),
  lastName: string().required(),
  genderUid: mixed().required(),
  raceUid: array().required(),
  crisis: string().required(),
  response: mixed().required(),
  dispositionUid: mixed().required(),
  officer: string().required(),
  referralSourceUid: mixed().required(),
  juvenile: string(),
  addr1: string().nullable(),
  addr2: string().nullable(),
  city: string().nullable(),
  stateUid: mixed().nullable(),
  postCode: number().nullable(),
  county: string().nullable(),
  officerCit: string(),
  shiftUid: mixed().required(),
  totalTimeOnCall: number(),
  type: string(),
  locationAddr1: string().nullable(),
  locationAddr2: string().nullable(),
  locationCity: string().nullable(),
  locationStateUid: mixed().nullable(),
  locationPostCode: number().nullable(),
  locationCounty: string().nullable(),
  otherReporting: string().nullable(),
  weaponInvolved: array(),
  injuryResultedFromForce: string().nullable().when(
    'forceUsedIncidentResultedInInjury',
    {
      is: (v: { value: string }[]) => v.length === 0 || (v.length === 1 && v[0].value === 'No Injury'),
      then: (s: any) => s.notRequired(),
      otherwise: (s: any) => s.required(),
    },
  ),
  forceUsedIncidentResultedInInjury: array().nullable().when(
    'response',
    {
      is: (v: any) => v === 'Force Used',
      then: (s: any) => s.required(),
      otherwise: (s: any) => s.notRequired(),
    },
  ),
  mHFollowUpRequested: string(),
  transportBy: string(),
  destination: string(),
  subjectTakenIntoCustodyForEvaluation: string(),
  incidentHasRelatedCrime: string(),
  incidentNote: string(),
  officerUnit: string(),
  reportNumber: string(),
  supervisor: string(),
  supervisorUnit: string(),
  priorityNote: string(),
  facilityUid: array().when('mHFollowUpRequested', {
    is: (v: any) => v === 'Yes',
    then: (s: any) => s.min(1, 'At least one facility must be selected').required(),
    otherwise: (s: any) => s.notRequired(),
  }),
  processInitiatedBy: mixed().when('subjectTakenIntoCustodyForEvaluation', {
    is: (v: any) => v === 'Yes',
    then: (s: any) => s.required(),
    otherwise: (s: any) => s.notRequired(),
  }),
  incidentHasRelatedCrimeYes: array().when('incidentHasRelatedCrime', {
    is: (v: any) => v === 'Yes',
    then: (s: any) => s.required(),
    otherwise: (s: any) => s.notRequired(),
  }),
  criminalChargesToBeFiled: mixed().when('incidentHasRelatedCrime', {
    is: (v: any) => v === 'Yes',
    then: (s: any) => s.required(),
    otherwise: (s: any) => s.notRequired(),
  }),
  approvalStatus: string(),
};

const validationSchema = object().shape(objectShape);

const initialValues = {
  ...Object.keys(objectShape)
    .reduce((a, m) => ({
      ...a,
      // @ts-ignore
      [m]: objectShape[m] instanceof string || objectShape[m] instanceof number ? '' : undefined,
    }), {}),
  dateTime: new Date(),
  phoneType: 'Mobile',
  code: '',
  officerName: '',
  officer: '',
  approvalStatus: 'pending',
  veteranStatus: veteranStatuses.find(o => o.value === 'Veteran status unknown'),
  facilityUid: [],
};

const initialStatus = {
  isFormLoaded: false,
};

export default {
  formData: {
    typeOptions,
    crisisEventOptions,
    responseOptions,
    weaponInvolvedOptions,
    transportByOptions,
    processInitiatedByOptions,
    incidentHasRelatedCrimeOptions,
    criminalChargesToBeFiledOptions,
    forceUsedIncidentResultedInInjuryOptions,
    phoneTypeOptions,
    veteranStatuses,
    yesNo,
  },
  transformOption,
  validationSchema,
  initialValues,
  initialStatus,
};
